/**
 * The one place the browser talks to the API. Every call goes through here, so
 * token refresh, error shape and sign-out live in exactly one file.
 */

/**
 * Where the API lives.
 *
 * On Netlify the API is a function mounted at `/api` on the same origin, so
 * nothing has to be configured — same-origin also means the session cookie
 * works without any CORS setup at all. Set VITE_API_URL only when the API is
 * hosted somewhere else (a separate Render service, or localhost in dev).
 */
const SAME_ORIGIN_API = '/api';
const BASE = (import.meta.env?.VITE_API_URL as string | undefined)
  ?? (import.meta.env?.DEV ? 'http://localhost:8080' : SAME_ORIGIN_API);
const TOKEN_KEY = 'cv.token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
try { token = localStorage.getItem(TOKEN_KEY); } catch { token = null; }

const listeners = new Set<(t: string | null) => void>();
export function onAuthChange(fn: (t: string | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function getToken(): string | null { return token; }
export function setToken(next: string | null): void {
  token = next;
  try {
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode; the session simply does not survive a reload */ }
  for (const fn of listeners) fn(next);
}

let refreshing: Promise<boolean> | null = null;

/** Per-call knobs. Only the importer needs one so far. */
export interface ApiOptions { signal?: AbortSignal }

async function raw(method: string, path: string, body?: unknown, opts: ApiOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  return fetch(BASE + path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'include',
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  if (text) { try { json = JSON.parse(text); } catch { json = null; } }
  if (!res.ok) {
    const e = (json as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    throw new ApiError(
      res.status,
      e?.code ?? 'unknown',
      e?.message ?? `The server returned ${res.status}. Try again in a moment.`,
      e?.details,
    );
  }
  return json as T;
}

/**
 * Requests retry exactly once behind a refresh. Anything more and a wrong
 * password would turn into a loop the person cannot see.
 */
export async function api<T>(method: string, path: string, body?: unknown, opts: ApiOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await raw(method, path, body, opts);
  } catch (e) {
    // A cancelled request is not a network failure, and telling somebody who
    // just pressed Stop to check their connection would be a lie.
    if (opts.signal?.aborted || (e instanceof Error && e.name === 'AbortError')) {
      throw new ApiError(0, 'aborted', 'That request was cancelled.');
    }
    throw new ApiError(0, 'offline', 'Cannot reach the server. Check your connection and try again.');
  }
  if (res.status === 401 && token && !path.startsWith('/v1/auth/')) {
    refreshing ??= (async () => {
      try {
        const r = await raw('POST', '/v1/auth/refresh');
        if (!r.ok) return false;
        const next = (await r.json()) as { token: string };
        setToken(next.token);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
    const ok = await refreshing;
    if (!ok) { setToken(null); throw new ApiError(401, 'unauthorized', 'Your session has ended. Sign in again.'); }
    res = await raw(method, path, body, opts);
  }
  return parse<T>(res);
}

export const get = <T>(p: string) => api<T>('GET', p);
export const post = <T>(p: string, b?: unknown, o?: ApiOptions) => api<T>('POST', p, b, o);
export const patch = <T>(p: string, b?: unknown) => api<T>('PATCH', p, b);
export const del = <T>(p: string) => api<T>('DELETE', p);

/* ------------------------------------------------------------- the shapes -- */

export interface Me {
  user: {
    id: string; email: string; firstName: string; lastName: string; role: string;
    phone: string | null; state: string | null; city: string | null;
    /** True when any second factor is enrolled. The three below say which. */
    mfaEnabled: boolean;
    mfaTotp: boolean;
    mfaPhone: boolean;
    mfaRecoveryLeft: number;
    onboardedAt: string | null;
  };
  org: {
    id: string; name: string; slug: string; timezone: string;
    onboardedAt: string | null;
    /** True when this agency requires a second factor of everybody. */
    requireMfa: boolean;
    /** True when everybody here sees every lead here. On unless turned off. */
    sharedLeads: boolean;
    /** True when all outbound calling is stopped by hand. */
    dialingPaused: boolean;
    dialingPausedBy: string | null;
    dialingPausedReason: string | null;
  } | null;
  teams: Array<{ teamId: string; role: 'leader' | 'member' }>;
  licences: Array<{ id: string; state: string; type: string; expiresAt: string | null; status: string }>;
  capabilities: string[];
  /** False when no text-message provider is configured, so phone codes cannot be sent. */
  smsAvailable: boolean;
}

export interface GateFailure { code: string; check: string; message: string }

export interface Lead {
  id: string; firstName: string | null; lastName: string | null; phone: string | null;
  email: string | null; state: string | null; city: string | null; zip: string | null;
  status: string; score: number; ownerId: string | null; ownerName: string | null;
  attempts: number; answeredCount: number; suppressed: boolean; suppressedReason: string | null;
  /**
   * Taken off the dial list because the number never worked. NOT suppression —
   * nobody asked for this, and one tap undoes it.
   */
  retiredAt: string | null;
  retiredReason: 'never_answered' | 'dead_air' | null;
  retiredBecause: string | null;
  createdAt: string; lastAttemptAt: string | null; dependents: number | null;
  requestedFaceCents: number | null; existingCoverageCents: number | null;
  /** ISO, pinned to UTC midnight. Read the day off it with `dobDayOfMonth`, never `getDate`. */
  dob: string | null;
  /** How their income arrives. Null means nobody has asked, which is not 'other'. */
  benefitSchedule: 'ss_birthday' | 'ss_third' | 'ssi_first' | 'other' | null;
  custom: Record<string, string>;
  scoreBreakdown: Array<{ ruleId: string; label: string; points: number }>;
  callableNow: boolean; minutesUntilOpen: number; localHour: number | null;
  gate: { ok: boolean; failures: GateFailure[]; warnings: GateFailure[] };
}

export interface TeamRow {
  id: string; name: string; description: string | null; homeState: string | null;
  homeCity: string | null; joinPolicy: 'open' | 'request' | 'closed';
  memberCount: number; leaderNames: string[]; pendingRequests: number;
  viewerRole: 'leader' | 'member' | null;
  viewerRequestStatus: string | null;
}

export interface OnboardingStep {
  key: string; title: string; blurb: string; done: boolean; current: boolean; optional: boolean;
}
export interface Onboarding {
  /** The caller's organisation role, which decides the second track below. */
  role: 'owner' | 'admin' | 'manager' | 'agent' | 'compliance';
  /**
   * The second track. It is the agency for an owner, the team for a manager
   * or team leader, and the compliance desk for a compliance officer — hence
   * the label rather than a fixed heading.
   */
  agency: {
    step: string; complete: boolean; steps: OnboardingStep[];
    label: string; blurb: string;
  };
  you: { step: string; complete: boolean; steps: OnboardingStep[] };
  next: { scope: 'agency' | 'you' | 'none'; key: string };
  counts: { users: number; teams: number; sources: number; leads: number };
}

export interface ImportPreview {
  importId: string;
  delimiter: string;
  /** Index of the header within the file's non-blank lines; rows start after it. */
  headerRow: number;
  header: string[];
  mapping: Record<string, number>;
  mappedBy: Record<string, string>;
  unmapped: string[];
  summary: {
    totalRows: number; usable: number; rejected: number; warnings: number;
    duplicatesInFile: number; missingPhone: number; missingState: number;
    missingConsentDate: number; stateFromZip: number; existingDuplicates: number;
  };
  sample: Array<{
    rowNumber: number; usable: boolean;
    issues: Array<{ level: string; field: string; message: string }>;
    lead: { firstName: string | null; lastName: string | null; phone: string | null; email: string | null; state: string | null; age: number | null; stateSource: string } | null;
  }>;
  rejects: Array<{ rowNumber: number; issues: Array<{ level: string; field: string; message: string }> }>;
}

/** The reply to opening a previewed import for streaming. */
export interface ImportOpened {
  importId: string;
  status: string;
  /** The server's own ceilings, so the client tunes itself down rather than guessing. */
  maxRowsPerBatch: number;
  maxRows: number;
}

/** What one batch of rows reports back: itself, and the import so far. */
export interface ImportBatchResult {
  importId: string;
  seq: number;
  duplicate: boolean;
  accepted: number;
  inserted: number;
  merged: number;
  skipped: number;
  rejected: number;
  totalRows: number;
  blockedOnArrival: number;
}

export interface ImportResult {
  importId: string;
  inserted: number;
  merged: number;
  skipped: number;
  rejected: number;
  totalRows: number;
  blockedOnArrival: number;
  duration_ms: number;
}
